'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/app/lib/utils';
import { Typography, type TypographyVariant } from '../Typography';
import { ButtonIcon } from '@/app/common';
import { modalVariants, type ModalVariants } from './modal.variants';

export interface ModalClassNames {
  modal?: string;
  header?: string;
  headerContent?: string;
  closeButton?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: ModalVariants['variant'];
  title?: string;
  titleVariant?: TypographyVariant;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  classNames?: ModalClassNames;
  ariaLabel?: string;
  closeButtonAriaLabel?: string;
  closeIconSize?: number;
  /** Max-width Tailwind class, e.g. 'max-w-lg'. Defaults to 'max-w-lg'. */
  maxWidth?: string;
}

function ModalBody({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex-1 overflow-y-auto flex flex-col p-4 md:px-8 pt-2 md:pt-4', className)}>{children}</div>
  );
}

function ModalFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex-shrink-0',
        'p-4 md:p-8 md:pt-0',
        'border-t border-secondary-100 md:border-0',
        'bg-secondary-50',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Modal({
  isOpen,
  onClose,
  variant = 'default',
  title,
  titleVariant = 'h4',
  subtitle,
  children,
  className,
  classNames,
  ariaLabel,
  closeButtonAriaLabel = 'Dismiss notice',
  closeIconSize,
  maxWidth,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isMedia = variant === 'media';
  const effectiveCloseIconSize = closeIconSize ?? (isMedia ? 24 : 16);
  const effectiveMaxWidth = maxWidth ?? (isMedia ? '' : 'max-w-lg');
  const {
    modal: modalSlot,
    header: headerSlot,
    headerContent: headerContentSlot,
    closeButton: closeButtonSlot,
  } = modalVariants({ variant });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      if (dialog.open) dialog.close();
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-label={ariaLabel}
      onClick={handleBackdropClick}
      onClose={onClose}
      className={cn(
        'fixed flex flex-col [&:not([open])]:hidden p-0 animate-fade-in backdrop:animate-backdrop-fade-in',
        modalSlot(),
        effectiveMaxWidth,
        className,
        classNames?.modal,
      )}
    >
      <div className={cn('flex-shrink-0 sticky top-0 z-10', headerSlot(), classNames?.header)}>
        <div className={cn('flex justify-end', headerContentSlot(), classNames?.headerContent)}>
          <ButtonIcon
            iconName="CloseBtn"
            iconSize={effectiveCloseIconSize}
            size="md"
            variant="icon"
            className={cn('cursor-pointer transition-colors duration-200', closeButtonSlot(), classNames?.closeButton)}
            onClick={onClose}
            aria-label={closeButtonAriaLabel}
          />
        </div>
        {title !== undefined && (
          <div className="px-4 md:px-8 pb-2">
            <Typography variant={titleVariant} className="mb-1 text-primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="bodySm" as="p">
                {subtitle}
              </Typography>
            )}
          </div>
        )}
      </div>
      {children}
    </dialog>
  );
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
